import { getPartialPaymentBalance } from 'utils/partial-payment-balance';
import { tipAfterCloverVoid, tipAfterPartialCloverVoid } from 'utils/payments';
import { useState } from 'react';
import { Formik, useFormikContext } from 'formik';

import { capitalize } from 'utils/string';
import { formatPrice } from 'utils/helpers';
import { clearExternalPaymentId } from 'utils/storage';
import { useVoidCloverPaymentMutation } from 'store/api/clover';
import {
  isMobile,
  useAlert,
  handleMutation,
  useSelectedCloverDevice,
} from 'hooks';
import {
  ButtonColors,
  PaymentMethod,
  CloverVoidReason,
  PaymentMethodType,
} from 'types';
import {
  Icon,
  Flex,
  List,
  Dialog,
  Button,
  ListCard,
  InfoBlock,
  ListHeader,
  DialogType,
  InputField,
  ButtonTypes,
  ErrorMessage,
  CommonStyles,
  DeleteButtonWithRef,
} from 'components';

import { Cash } from './cash';
import { Check } from './check';
import { Credit } from './credit';
import * as Styles from './styles';
import { PaymentMethodsProps } from './types';
import { ManualCredit } from './manual-credit';
import { initialValues, getValidationSchema } from './config';

const template = '1fr 1fr 24px';
const titles = ['Payment method', 'Amount'];

export const CheckoutPaymentMethods = <
  T extends { tip?: number; paymentMethods?: PaymentMethod[] },
>({
  company,
  eventData,
  saleTotal,
  checkoutId,
  setFormValues,
  onRemoveCredit,
  onAddCreditPaymentMethod,
}: PaymentMethodsProps<T>) => {
  const mobile = isMobile();
  const [protectedPayment, setProtectedPayment] = useState(false);
  const { showSuccessAlert } = useAlert();
  const { errors, values, touched } = useFormikContext<T>();
  const { selectedDeviceId: deviceId } = useSelectedCloverDevice(eventData);

  const [cloverVoid, mutation] = useVoidCloverPaymentMutation();
  handleMutation(mutation);

  const cards = [
    {
      disabled: true,
      icon: <Icon.Gift />,
      title: 'Gift card / Store credit',
    },
  ];

  const isProcessingFeeEnabled = company?.isProcessingFeeEnabled;
  const isManualCreditPaymentEnabled = eventData?.isManualCreditPaymentEnabled;

  const getFilteredPaymentMethods = (
    paymentMethods: PaymentMethod[] = [],
    method: PaymentMethod
  ) =>
    paymentMethods.filter((value) => {
      if (method.cloverPaymentId && value.cloverPaymentId) {
        return value.cloverPaymentId !== method.cloverPaymentId;
      }

      return value.id !== method.id;
    });

  const removeMethod = (method: PaymentMethod) =>
    setFormValues((prev) => ({
      ...prev,
      paymentMethods: getFilteredPaymentMethods(prev.paymentMethods, method),
    }));

  const onAddPaymentMethod = (method: PaymentMethod) =>
    !protectedPayment &&
    setFormValues((prev) => ({
      ...prev,
      paymentMethods: [...(prev.paymentMethods ?? []), method],
    }));

  const onRemoveConfirm = async (
    item: PaymentMethod,
    approvalPassword: string,
    close: () => void
  ) => {
    if (protectedPayment) return;
    console.log('[Checkout] start remove payment method:', {
      item,
      type: item.type,
    });

    if (item.type !== PaymentMethodType.Credit) {
      removeMethod(item);
      close();
      return;
    }

    if (
      item.type === PaymentMethodType.Credit &&
      isManualCreditPaymentEnabled
    ) {
      removeMethod(item);
      close();
      return;
    }

    try {
      console.log('[Checkout] start void clover payment');
      const response = await cloverVoid({
        approvalPassword,
        id: item!.cloverPaymentId!,
        deviceId: Number(deviceId),
        eventId: Number(eventData?.id),
        voidReason: CloverVoidReason.UserCancel,
      }).unwrap();

      if (response?.id) {
        close();
        clearExternalPaymentId(String(eventData?.id));
        setFormValues((prev) => ({
          ...prev,
          tip: getPartialPaymentBalance({
            paymentMethods: prev.paymentMethods ?? [],
            saleTotal,
            currentTip: prev.tip,
          })
            ? tipAfterPartialCloverVoid(prev.paymentMethods ?? [], item)
            : tipAfterCloverVoid(prev.tip, item),
          paymentMethods: getFilteredPaymentMethods(prev.paymentMethods, item),
        }));
        showSuccessAlert('Payment method successful voided');

        onRemoveCredit?.(response);

        console.log('[Checkout] finished void clover payment', { response });
      }
    } catch (error) {
      console.error(error);
      console.log('[Checkout] finished void clover payment with error', error);
    }
  };

  const partialBalance = getPartialPaymentBalance({
    paymentMethods: values.paymentMethods ?? [],
    saleTotal,
    currentTip: values.tip,
  });

  const commonPaymentProps = {
    amount: saleTotal,
    onAddPaymentMethod,
    isProcessingFeeEnabled,
  };

  return (
    <Flex gap={mobile ? 16 : 40} flexDirection="column">
      <Flex gap={16} flexDirection="column">
        {errors?.paymentMethods && touched?.paymentMethods && (
          <ErrorMessage>{errors.paymentMethods as string}</ErrorMessage>
        )}

        {partialBalance &&
          partialBalance.remaining > 0 &&
          !protectedPayment && (
            <div
              role="status"
              style={{
                padding: '20px 24px',
                borderRadius: 12,
                background: '#f2f6ff',
                border: '1px solid #cbdafa',
                lineHeight: 1.6,
              }}
            >
              <h3 style={{ margin: '0 0 8px' }}>
                {formatPrice(partialBalance.paid / 100)} paid ·{' '}
                {formatPrice(partialBalance.remaining / 100)} remaining
              </h3>
              {partialBalance.tipRemaining > 0 && (
                <p style={{ margin: '4px 0' }}>
                  The remaining balance includes the{' '}
                  {formatPrice(partialBalance.tipRemaining / 100)} tip already
                  selected.
                </p>
              )}
              <p style={{ margin: '4px 0' }}>
                Choose <strong>Credit</strong> to pay the rest with another
                card.
              </p>
              <p style={{ margin: '4px 0' }}>
                No additional tip will be requested.
              </p>
            </div>
          )}
        {partialBalance?.overpaid && (
          <ErrorMessage>
            Payments exceed the total. Review the recorded payments before
            continuing.
          </ErrorMessage>
        )}
        <Styles.Cards>
          {protectedPayment ? (
            <InfoBlock disabled>
              <Styles.CardContainer>
                <Icon.Cash />
                <h3>Cash</h3>
              </Styles.CardContainer>
            </InfoBlock>
          ) : (
            <Cash {...commonPaymentProps} />
          )}

          {isManualCreditPaymentEnabled ? (
            <ManualCredit {...commonPaymentProps} />
          ) : (
            <Credit
              {...commonPaymentProps}
              partialBalance={partialBalance}
              event={eventData}
              checkoutId={checkoutId}
              onPaymentProtectionChange={setProtectedPayment}
              onAddCreditPaymentMethod={onAddCreditPaymentMethod}
            />
          )}

          {protectedPayment ? (
            <InfoBlock disabled>
              <Styles.CardContainer>
                <Icon.CheckCard />
                <h3>Check</h3>
              </Styles.CardContainer>
            </InfoBlock>
          ) : (
            <Check {...commonPaymentProps} />
          )}
          {cards.map(({ icon, title, disabled }) => (
            <InfoBlock key={title} disabled={disabled}>
              <Styles.CardContainer>
                {icon}
                <h3>{title}</h3>
              </Styles.CardContainer>
            </InfoBlock>
          ))}
        </Styles.Cards>
      </Flex>

      {values?.paymentMethods?.length ? (
        <List
          mainWrapperStyles={{ padding: 0 }}
          count={values?.paymentMethods?.length}
          header={
            <ListHeader padding="8px" titles={titles} template={template} />
          }
        >
          {values?.paymentMethods?.map((item) => {
            const type = capitalize(item.type);
            const amount = formatPrice(item.totalAmount, '$0.00');
            const isCashPayment = item.type === PaymentMethodType.Cash;
            const isCreditCloverPayment =
              item.type === PaymentMethodType.Credit &&
              !isManualCreditPaymentEnabled;

            const receivedAmount = item.receivedCashAmount
              ? formatPrice(item.receivedCashAmount)
              : amount;

            return (
              <ListCard
                key={item.id}
                headers={titles}
                template={template}
                tabletTemplate={template}
                mobileTemplate={template}
                values={[
                  `${type} ${isCreditCloverPayment ? `Card ${item.last4CC}` : ''}`,
                  `${amount} ${isCashPayment ? `(Received ${receivedAmount})` : ''}`,
                  <Dialog
                    title="Delete payment method"
                    trigger={
                      <DeleteButtonWithRef
                        disabled={mutation.isLoading || protectedPayment}
                      />
                    }
                  >
                    {(close, _, ButtonWrap) => (
                      <Formik
                        initialValues={initialValues}
                        validationSchema={getValidationSchema(
                          isCreditCloverPayment
                        )}
                        onSubmit={(values) =>
                          onRemoveConfirm(item, values.approvalPassword, close)
                        }
                      >
                        {({ isSubmitting }) => (
                          <CommonStyles.Form gap={24}>
                            <Flex column gap={16}>
                              <p>
                                You are going to delete payment method{' '}
                                <span className="semibold">{type}</span> with
                                amount{' '}
                                <span className="semibold">{amount}</span> Are
                                you sure you want to do that?
                              </p>

                              {isCreditCloverPayment && (
                                <InputField
                                  number
                                  autofill="off"
                                  label="Manager PIN"
                                  name="approvalPassword"
                                  placeholder="Manager PIN"
                                  autoComplete="one-time-code"
                                />
                              )}
                            </Flex>

                            <ButtonWrap type={DialogType.Plain}>
                              <Button
                                text="Close"
                                onClick={close}
                                colorType={ButtonColors.LightBlue}
                              />

                              <Button
                                text="Delete"
                                disabled={isSubmitting}
                                type={ButtonTypes.Submit}
                                colorType={ButtonColors.LightRed}
                              />
                            </ButtonWrap>
                          </CommonStyles.Form>
                        )}
                      </Formik>
                    )}
                  </Dialog>,
                ]}
              />
            );
          })}
        </List>
      ) : undefined}
    </Flex>
  );
};
